import React, { useEffect, useRef, useState } from "react";
import { InventoryItem, InventoryStatus } from "../../types/inventoryItem.type";
import { api } from "../../api/api";
import {
  Button,
  DatePicker,
  Dropdown,
  Form,
  Image,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import { Currency } from "../../types/currency.type";
import { UnitOfMeasurement } from "../../types/unitOfMeasurement.type";
import { useAuth } from "../../contexts/auth.context";
import useGetRequest from "../../hooks/useGetRequest";
import { UserRole } from "../../types/user.type";
import { canEditInventory } from "../../utils/roleUtils";
import { Cashbox } from "../../types/cashbox.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { useNavigate } from "react-router-dom";
import { API } from "../../config/config";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { useExchangeRate } from "../../contexts/exchangeRate.context";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;
export default function Inventory() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [form] = Form.useForm();
  const [saleForm] = Form.useForm();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [dateRange, setDateRange] = useState<[any, any] | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaleOpen, setIsSaleOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [status, setStatus] = useState<InventoryStatus[]>([]);
  const [currency, setCurrency] = useState<Currency | null>(null);
  const [stats, setStats] = useState({
    totalQuantity: 0,
    totalAmountUZS: 0,
    totalAmountUSD: 0,
  });
  const [totalCost, setTotalCost] = useState({
    USD: 0,
    UZS: 0,
  });
  const { data: cashboxesList, loading: cashboxListLoading } =
    useGetRequest(`/cashbox/list`);
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { exchangeRate } = useExchangeRate();

  const handleExport = () => {
    downloadFile("/export/inventory", "inventory_data.xlsx");
  };

  const calculateTotalCost = (values: any) => {
    const { currency, quantity, price, exchangeRate } = values;

    if (
      !currency ||
      quantity === undefined ||
      price === undefined ||
      exchangeRate === undefined
    )
      return;

    let costInUSD = quantity * price;

    if (currency === Currency.US_DOLLAR) {
      setTotalCost({
        USD: costInUSD,
        UZS: costInUSD * exchangeRate,
      });
    } else {
      setTotalCost({
        USD: (price / exchangeRate) * quantity,
        UZS: costInUSD,
      });
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await api.get("/inventory", {
        params: {
          page,
          limit: pageSize,
          sortBy,
          order: sortOrder,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
          status,
          currency,
          search: searchTerm,
        },
      });

      const { items, total, totalQuantity, totalAmountUZS, totalAmountUSD } =
        response.data;

      setItems(items);
      setTotal(total);
      setStats({
        totalQuantity,
        totalAmountUZS,
        totalAmountUSD,
      });
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение инвентаря.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [
    page,
    pageSize,
    sortBy,
    sortOrder,
    dateRange,
    status,
    currency,
    searchTerm,
  ]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  const handleSubmit = async (values: Partial<InventoryItem>) => {
    setConfirmLoading(true);

    const formData = new FormData();

    Object.keys(values).forEach((key) => {
      const fieldKey = key as keyof InventoryItem;
      if (values[fieldKey] !== undefined) {
        formData.append(fieldKey as string, values[fieldKey] as any);
      }
    });

    if (file) {
      formData.append("photo", file);
    }
    formData.append("exchangeRate", (exchangeRate || 0).toString());

    try {
      if (selectedItem) {
        await api.put(`/inventory/${selectedItem._id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        message.success("Элемент успешно обновлен.");
      } else {
        await api.post("/inventory", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        message.success("Элемент успешно добавленю");
      }

      setIsModalOpen(false);
      setConfirmLoading(false);
      fetchItems();
      form.resetFields();
      setFile(null);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving item:", error);
      message.error(error.response.data.error || "Ошибка.");
    }
  };

  const handleSale = async (values: any) => {
    setConfirmLoading(true);
    try {
      await api.post(`/inventory/sale/${selectedItem?._id}`, values);

      message.success("Элемент успешно продан.");

      setIsSaleOpen(false);
      setConfirmLoading(false);
      fetchItems();
      saleForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving item:", error);
      message.error(error.response.data.error || "Ошибка.");
    }
  };

  const handleOpenModal = (item?: InventoryItem) => {
    if (item) {
      setSelectedItem(item);
      form.setFieldsValue(item);
    } else {
      setSelectedItem(null);
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsSaleOpen(false);
    setSelectedItem(null);
  };

  const handleSaleOpen = (item: InventoryItem) => {
    setSelectedItem(item);
    setIsSaleOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleCustomButtonClick = () => {
    fileInputRef.current?.click();
  };

  const columns = [
    {
      title: "Трек ID",
      dataIndex: "trackingId",
      key: "trackingId",
    },
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Фото",
      key: "photoUri",
      dataIndex: "photoUri",
      render: (photoUri: string) => {
        if (!photoUri) return "Нет";
        return (
          <div>
            <Image
              key={photoUri}
              src={`${API}/${photoUri}`}
              width={50}
              height={50}
              preview={{ src: `${API}/${photoUri}` }}
              alt="Фото"
              loading="lazy"
              style={{
                cursor: "pointer",
                borderRadius: 4,
                border: "1px solid #ddd",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
              }}
            />
          </div>
        );
      },
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Количество",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Ед.измерения",
      dataIndex: "unitOfMeasurement",
      key: "unitOfMeasurement",
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Добавил",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Последнее обновление",
      dataIndex: "lastUpdate",
      key: "lastUpdate",
    },
    {
      title: "Цена",
      dataIndex: "price",
      key: "price",
    },
    {
      title: "Общая стоимость",
      dataIndex: "total",
      key: "total",
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, item: InventoryItem) => {
        const menu = (
          <Menu>
            <Menu.Item
              onClick={() => navigate(`${item._id}/${item.name}/logs`)}
            >
              Смотреть журнал
            </Menu.Item>
            {item.status !== InventoryStatus.SOLD && (
              <>
                {(authState.user?.role === UserRole.Admin ||
                  authState.user?.role === UserRole.Cashier) && (
                  <Menu.Item onClick={() => handleSaleOpen(item)}>
                    Продать
                  </Menu.Item>
                )}
                {canEditInventory(authState.user?.role) && (
                  <Menu.Item onClick={() => handleOpenModal(item)}>
                    Изменить
                  </Menu.Item>
                )}
              </>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Инвентарь</Title>
      <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <RangePicker
          onChange={(dates) => setDateRange(dates ?? undefined)}
          placeholder={["Начало", "Конец"]}
          format={{ format: "DD.MM.YY" }}
        />
        <Select
          placeholder="Сортировать по"
          defaultValue="date"
          onChange={(value) => setSortBy(value)}
          style={{ width: 120 }}
        >
          <Option value="date">Дата</Option>
          <Option value="price">Цена</Option>
          <Option value="total">Сумма</Option>
          <Option value="quantity">Кол-во</Option>
        </Select>
        <Select
          defaultValue="desc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 150 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
        <Select
          allowClear
          placeholder="Фильтр по статусу"
          onChange={(value) => setStatus(value)}
          style={{ width: 300 }}
          mode="multiple"
        >
          {Object.values(InventoryStatus).map((status) => (
            <Option key={status} value={status}>
              {status}
            </Option>
          ))}
        </Select>
        <Select
          allowClear
          placeholder="Фильтр по валюте"
          onChange={(value) => setCurrency(value)}
          style={{ width: 150 }}
        >
          {Object.values(Currency).map((Currency) => (
            <Option key={Currency} value={Currency}>
              {Currency}
            </Option>
          ))}
        </Select>
        {canEditInventory(authState.user?.role) && (
          <Button type="primary" onClick={() => handleOpenModal()}>
            Добавить
          </Button>
        )}
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Text>Всего кол-во: {stats.totalQuantity}</Text>
        <Text>Сумма USD: {roundToThreeDecimals(stats.totalAmountUSD)}</Text>
        <Text>Сумма UZS: {roundToThreeDecimals(stats.totalAmountUZS)}</Text>
      </Space>

      <Table
        loading={loading}
        columns={columns}
        dataSource={items}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title={selectedItem ? "Изменить элемент" : "Добавить элемент"}
        open={isModalOpen}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Название"
            rules={[
              { required: true, message: "Пожалуйста, введите название" },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Описание"
            rules={[
              { required: true, message: "Пожалуйста, введите описание" },
            ]}
          >
            <Input.TextArea />
          </Form.Item>

          <Form.Item
            name="quantity"
            label="Кол-во"
            rules={[
              { required: true, message: "Пожалуйста, введите количество" },
            ]}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="price"
            label="Цена"
            rules={[{ required: true, message: "Пожалуйста, введите цену" }]}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: "Пожалуйста, выберете валюту" }]}
          >
            <Select allowClear style={{ width: 200 }}>
              {Object.values(Currency).map((currency) => (
                <Option key={currency} value={currency}>
                  {currency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="unitOfMeasurement"
            label="Ед.измерения"
            rules={[
              {
                required: true,
                message: "Пожалуйста, выберете ед.измерения",
              },
            ]}
          >
            <Select allowClear style={{ width: 200 }}>
              {Object.values(UnitOfMeasurement).map((unit) => (
                <Option key={unit} value={unit}>
                  {unit}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="photo" label="Фото" valuePropName="file">
            <Button onClick={handleCustomButtonClick} type="default">
              Выбрать фото
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {file && (
              <div style={{ marginTop: 10 }}>
                <img
                  src={URL.createObjectURL(file)}
                  alt="Selected"
                  style={{ width: 100, height: 100, objectFit: "cover" }}
                />
              </div>
            )}
          </Form.Item>
          <Form.Item
            name="status"
            label="Статус"
            rules={[{ required: true, message: "Пожалуйста, выберете статус" }]}
          >
            <Select allowClear style={{ width: 200 }}>
              {Object.values(InventoryStatus)
                .filter((status) => status !== InventoryStatus.SOLD)
                .map((status) => (
                  <Option key={status} value={status}>
                    {status}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Продать"
        open={isSaleOpen}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        onOk={() => saleForm.submit()}
      >
        <Form
          form={saleForm}
          layout="vertical"
          onFinish={handleSale}
          onValuesChange={(_, allValues) => calculateTotalCost(allValues)}
        >
          <Form.Item
            name="cashboxId"
            label="Касса"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите кассу" loading={cashboxListLoading}>
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Option key={cashbox._id} value={cashbox._id}>
                  Имя: {cashbox.name} | Валюта: {cashbox.currency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select
              placeholder="Выберите валюту"
              onChange={(value) => setCurrency(value)}
            >
              {Object.values(Currency).map((currency) => (
                <Option key={currency} value={currency}>
                  {currency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="price"
            label="Цена"
            rules={[{ required: true, message: "Пожалуйста, введите цену" }]}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="quantity"
            label="Кол-во"
            rules={[
              { required: true, message: "Пожалуйста, введите количество" },
            ]}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            rules={[{ required: true, message: "Пожалуйста, введите курс" }]}
            hidden={true}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите описание." }]}
          >
            <Input.TextArea />
          </Form.Item>
          <div style={{ marginTop: 16, fontWeight: "bold" }}>
            Общая стоимость:
            <br />
            USD: {totalCost.USD.toFixed(2)}
            <br />
            UZS: {totalCost.UZS.toFixed(2)}
          </div>
        </Form>
      </Modal>
    </div>
  );
}
